/**
 * Product Hero Web Component
 * Handles variant selection, quantity control, info accordion, tabs, and accessory add-to-cart
 */

if (!customElements.get('product-hero')) {
  customElements.define('product-hero', class ProductHero extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.variantsJson = document.getElementById(`ProductVariants-${this.sectionId}`);
      this.variants = this.variantsJson ? JSON.parse(this.variantsJson.textContent) : [];

      this.variantIdInputs = this.querySelectorAll('input[name="id"]');
      this.atcBtns = this.querySelectorAll('.product-hero__atc-btn, .product-hero__sticky-bar-btn');
      this.stickyBarAmount = this.querySelector('.product-hero__sticky-bar-amount');

      this.currentIndex = 0;
      this.slides = this.querySelectorAll('.product-hero__slide');
      this.thumbs = this.querySelectorAll('.product-hero__thumb');

      const stickyBar = this.querySelector('.product-hero__sticky-bar');
      this.currentUnitPrice = stickyBar ? parseInt(stickyBar.dataset.unitPrice, 10) || 0 : 0;
    }

    connectedCallback() {
      this.initQuantitySelector();
      this.initVariantSelector();
      this.initInfoAccordion();
      this.initSlider();
      this.initTabs();
      this.initAccessories();
    }

    /* ========== Quantity Selector ========== */
    initQuantitySelector() {
      const qtySelector = this.querySelector('.product-hero__quantity-selector');
      if (!qtySelector) return;

      const input = qtySelector.querySelector('.product-hero__qty-input');
      const hiddenInput = this.querySelector(`#QuantityHidden-${this.sectionId}`);
      const barHiddenInput = this.querySelector(`#QuantityBarHidden-${this.sectionId}`);

      const updateQuantity = (val) => {
        input.value = val;
        if (hiddenInput) hiddenInput.value = val;
        if (barHiddenInput) barHiddenInput.value = val;
        this.updateStickyBarPrice();
      };

      qtySelector.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;

        e.preventDefault();
        const action = btn.getAttribute('data-action');
        let val = parseInt(input.value) || 1;

        if (action === 'increase') val++;
        if (action === 'decrease') val = Math.max(1, val - 1);

        updateQuantity(val);
      });

      input.addEventListener('change', () => {
        const val = Math.max(1, parseInt(input.value) || 1);
        updateQuantity(val);
      });
    }

    /* ========== Variant Selector ========== */
    initVariantSelector() {
      const variantsContainer = this.querySelector('.product-hero__variants');
      if (!variantsContainer || this.variants.length === 0) return;

      variantsContainer.addEventListener('click', (e) => {
        const swatch = e.target.closest('.product-hero__swatch');
        if (!swatch || swatch.classList.contains('product-hero__swatch--unavailable')) return;

        const variantId = swatch.dataset.variantId;
        const variant = this.variants.find(v => String(v.id) === String(variantId));
        if (!variant) return;

        // Update active state
        const group = swatch.closest('.product-hero__swatches');
        group.querySelectorAll('.product-hero__swatch--active').forEach(s => {
          s.classList.remove('product-hero__swatch--active');
        });
        swatch.classList.add('product-hero__swatch--active');

        // Update hidden variant ID inputs
        this.variantIdInputs.forEach(input => { input.value = variant.id; });

        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Update sticky bar price (quantity-aware)
        this.currentUnitPrice = variant.price;
        this.updateStickyBarPrice();

        // Update ATC button state
        this.atcBtns.forEach(btn => {
          if (variant.available) {
            btn.removeAttribute('disabled');
            btn.textContent = window.variantStrings?.addToCart || 'Add to Cart';
          } else {
            btn.setAttribute('disabled', 'disabled');
            btn.textContent = window.variantStrings?.soldOut || 'Sold Out';
          }
        });

        // Update sticky bar name to variant name
        const stickyName = this.querySelector('.product-hero__sticky-bar-name');
        if (stickyName && variant.name) stickyName.textContent = variant.name;

        // Swap main slide to variant's featured image
        const mediaId = variant.featured_media?.id || variant.featured_image?.id;
        if (mediaId && this.slides.length > 0) {
          const targetIndex = Array.from(this.slides).findIndex(s => String(s.dataset.mediaId) === String(mediaId));
          if (targetIndex > -1) this.goToSlide(targetIndex);
        }
      });
    }

    /* ========== Info Accordion ========== */
    initInfoAccordion() {
      const accordion = this.querySelector('.product-hero__info-accordion');
      if (!accordion) return;

      accordion.addEventListener('click', (e) => {
        const header = e.target.closest('.product-hero__info-item-header');
        if (!header) return;

        const item = header.closest('.product-hero__info-item--expandable');
        if (!item) return;

        e.preventDefault();
        const body = item.querySelector('.product-hero__info-item-body');
        if (!body) return;

        const isOpen = item.classList.contains('product-hero__info-item--open');

        // Close all other items
        accordion.querySelectorAll('.product-hero__info-item--open').forEach((openItem) => {
          if (openItem !== item) {
            const openBody = openItem.querySelector('.product-hero__info-item-body');
            if (openBody) openBody.style.maxHeight = '0';
            openItem.classList.remove('product-hero__info-item--open');
          }
        });

        if (isOpen) {
          body.style.maxHeight = '0';
          item.classList.remove('product-hero__info-item--open');
        } else {
          body.style.maxHeight = body.scrollHeight + 'px';
          item.classList.add('product-hero__info-item--open');
        }
      });
    }

    /* ========== Slider ========== */
    initSlider() {
      if (this.slides.length <= 1) return;

      // Arrow controls
      const prevBtn = this.querySelector('.product-hero__arrow--prev');
      const nextBtn = this.querySelector('.product-hero__arrow--next');

      if (prevBtn) prevBtn.addEventListener('click', () => this.goToSlide(this.prevIndex()));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goToSlide(this.nextIndex()));

      // Thumbnail click navigation
      this.thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.dataset.thumbIndex, 10);
          this.goToSlide(index);
        });
      });

      // Touch/swipe support
      this.initSwipe();
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length || index === this.currentIndex) return;

      this.slides[this.currentIndex].classList.remove('product-hero__slide--active');
      this.currentIndex = index;
      this.slides[this.currentIndex].classList.add('product-hero__slide--active');

      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('product-hero__thumb--active', i === this.currentIndex);
      });
    }

    nextIndex() {
      return (this.currentIndex + 1) % this.slides.length;
    }

    prevIndex() {
      return (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    }

    initSwipe() {
      const slider = this.querySelector('.product-hero__slider');
      if (!slider) return;

      let startX = 0;
      let startY = 0;
      let tracking = false;

      slider.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });

      slider.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;

        const diffX = e.changedTouches[0].clientX - startX;
        const diffY = e.changedTouches[0].clientY - startY;

        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
          if (diffX < 0) {
            this.goToSlide(this.nextIndex());
          } else {
            this.goToSlide(this.prevIndex());
          }
        }
      }, { passive: true });
    }

    /* ========== Tab Navigation ========== */
    initTabs() {
      const tabs = this.querySelectorAll('.product-hero__tab');
      if (tabs.length === 0) return;

      tabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();

          const targetId = tab.getAttribute('href').substring(1);
          const targetSection = document.getElementById(targetId);

          if (targetSection) {
            // Update active state
            tabs.forEach(t => t.classList.remove('product-hero__tab--active'));
            tab.classList.add('product-hero__tab--active');

            // Smooth scroll to section
            const offset = 100; // Account for header
            const targetPosition = targetSection.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top: targetPosition, behavior: 'smooth' });
          }
        });
      });

      // Update active tab on scroll (optional)
      if (window.IntersectionObserver) {
        const observerOptions = {
          rootMargin: '-20% 0px -70% 0px',
          threshold: 0
        };

        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const sectionId = entry.target.id;
              const activeTab = this.querySelector(`[data-tab-link="${sectionId}"]`);
              if (activeTab) {
                tabs.forEach(t => t.classList.remove('product-hero__tab--active'));
                activeTab.classList.add('product-hero__tab--active');
              }
            }
          });
        }, observerOptions);

        // Observe all sections linked from tabs
        tabs.forEach(tab => {
          const targetId = tab.getAttribute('href').substring(1);
          const targetSection = document.getElementById(targetId);
          if (targetSection) observer.observe(targetSection);
        });
      }
    }

    /* ========== Accessories Add-to-Cart ========== */
    initAccessories() {
      const accessoriesContainer = this.querySelector('.product-hero__accessories');
      if (!accessoriesContainer) return;

      accessoriesContainer.addEventListener('click', (e) => {
        const addBtn = e.target.closest('.product-hero__accessory-add');
        if (!addBtn || addBtn.classList.contains('product-hero__accessory-add--loading')) return;

        const variantId = addBtn.dataset.variantId;
        if (!variantId) return;

        addBtn.classList.add('product-hero__accessory-add--loading');
        addBtn.disabled = true;

        window.addToCart([{ id: parseInt(variantId), quantity: 1 }])
          .then(() => {
            // Show success state
            addBtn.classList.add('product-hero__accessory-add--added');
            addBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';

            // Reset button after 2 seconds
            setTimeout(() => {
              addBtn.classList.remove('product-hero__accessory-add--added', 'product-hero__accessory-add--loading');
              addBtn.disabled = false;
              addBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
            }, 2000);
          })
          .catch(() => {
            addBtn.classList.remove('product-hero__accessory-add--loading');
            addBtn.disabled = false;
          });
      });
    }

    /* ========== Sticky Bar Price (quantity × unit price) ========== */
    updateStickyBarPrice() {
      if (!this.stickyBarAmount) return;

      const qtyInput = this.querySelector('.product-hero__qty-input');
      const qty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

      this.stickyBarAmount.textContent = this.formatMoney(this.currentUnitPrice * qty);
    }

    /* ========== Helpers ========== */
    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  });
}
